<template>
  <Header/>
  <div class="container is-max-desktop my-bookings">
    <h1>Administrator dashboard</h1>

    <h2>Opret bruger</h2>
    <p>
      Angiv e-mail og husstand. Brugeren kan logge ind med Google.
      Afkryds valgfrit «Tillad også login med Mail + kode» og angiv en kode.
    </p>

    <form class="form-section max-width" @submit.prevent="createUser">
      <div class="field">
        <label class="label" for="admin-email">E-mail</label>
        <div class="control">
          <input
            id="admin-email"
            v-model="form.email"
            class="input"
            type="email"
            required
            autocomplete="off"
          >
        </div>
      </div>
      <div class="field">
        <label class="label" for="admin-account">Husstand</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select id="admin-account" v-model.number="form.account" required>
              <option disabled :value="0">Vælg husstand</option>
              <option v-for="account in accounts" :key="account.id" :value="account.id">
                {{ account.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="field">
        <label class="label" for="admin-display-name">Visningsnavn (valgfrit)</label>
        <div class="control">
          <input
            id="admin-display-name"
            v-model="form.displayName"
            class="input"
            type="text"
            autocomplete="off"
          >
        </div>
      </div>
      <div class="field">
        <label class="label" for="admin-phone">Telefon (valgfrit)</label>
        <div class="control">
          <input
            id="admin-phone"
            v-model="form.phone"
            class="input"
            type="tel"
            autocomplete="off"
          >
        </div>
      </div>
      <div class="field">
        <label class="checkbox">
          <input v-model="form.allowPasswordLogin" type="checkbox">
          Tillad også login med Mail + kode
        </label>
        <p class="help">
          Google-login virker altid. Med afkrydsning kan brugeren også logge ind med mail + kode.
        </p>
      </div>
      <div v-if="form.allowPasswordLogin" class="field">
        <label class="label" for="admin-password">Kode</label>
        <div class="control">
          <input
            id="admin-password"
            v-model="form.password"
            class="input"
            type="text"
            required
            minlength="4"
            autocomplete="new-password"
            placeholder="Mindst 4 tegn"
          >
        </div>
      </div>
      <div class="field">
        <label class="checkbox">
          <input v-model="form.isAdmin" type="checkbox">
          Administrator
        </label>
      </div>
      <div v-if="formError" class="notification is-danger">{{ formError }}</div>
      <div v-if="formSuccess" class="notification is-success">{{ formSuccess }}</div>
      <button class="button is-success" type="submit" :disabled="saving">
        {{ saving ? 'Opretter…' : 'Opret bruger' }}
      </button>
    </form>

    <h2>Brugere</h2>
    <div v-if="tableError" class="notification is-danger is-light">{{ tableError }}</div>
    <div v-if="users.length > 0" class="table-container">
      <table class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th
              v-for="col in userColumns"
              :key="col.key"
              class="is-clickable"
              :aria-sort="sortAria(col.key)"
              @click="sortBy(col.key)"
            >
              {{ col.label }}{{ sortMarker(col.key) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in sortedUsers" :key="user.id">
            <td>{{ user.email }}</td>
            <td>{{ user.displayName }}</td>
            <td class="phone-cell">
              <input
                class="input is-small"
                type="tel"
                :value="normalizePhone(user.phone)"
                :disabled="savingPhoneId === user.id"
                @change="savePhone(user, $event)"
              >
            </td>
            <td>{{ accountName(user.account) }}</td>
            <td>{{ formatLogin(user.updated) }}</td>
            <td>{{ user.accessLvl === 1 ? 'Ja' : '' }}</td>
            <td>{{ user.hasClerk ? 'Ja' : '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else>Ingen brugere hentet.</p>

    <LoginStatus />
  </div>
  <Footer/>
</template>

<script>
import ax from 'axios'
export const axios = ax

import { defineComponent } from 'vue'
import { useComposition } from '@/compositions/composition'

import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import LoginStatus from '@/components/LoginStatus.vue'
import { normalizePhone } from '@/phone'

export default defineComponent({
  setup() {
    const { global } = useComposition();
    const formatLogin = (date) => {
      if (!date) return '';
      const raw = String(date).replace('T', ' ');
      if (raw.length < 16) return raw;
      return raw.substring(8, 10) + '.' + parseInt(raw.substring(5, 7), 10) + ' '
        + raw.substring(0, 4) + ' kl. ' + raw.substring(11, 16);
    };
    return {
        global,
        siteRoot: process.env.VUE_APP_SITE_ROOT,
        apiRoot: process.env.VUE_APP_API_ROOT,
        formatLogin,
        normalizePhone
    }
  },
  data() {
    return {
      accounts: [],
      users: [],
      sortKey: 'email',
      sortDir: 'asc',
      userColumns: [
        { key: 'email', label: 'E-mail' },
        { key: 'displayName', label: 'Navn' },
        { key: 'phone', label: 'Telefon' },
        { key: 'account', label: 'Husstand' },
        { key: 'updated', label: 'Seneste login' },
        { key: 'accessLvl', label: 'Admin' },
        { key: 'hasClerk', label: 'Google (Clerk)' },
      ],
      saving: false,
      savingPhoneId: 0,
      formError: '',
      formSuccess: '',
      tableError: '',
      form: {
        email: '',
        account: 0,
        displayName: '',
        phone: '',
        allowPasswordLogin: false,
        password: '',
        isAdmin: false
      }
    }
  },
  computed: {
    sortedUsers() {
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return [...this.users].sort((a, b) => {
        const av = this.sortValue(a, key);
        const bv = this.sortValue(b, key);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
  },
  components: {
    Header,
    Footer,
    LoginStatus,
  },
  created() {
      this.loadAdminData();
  },
  methods: {
      accountName(accountId) {
        const account = this.accounts.find((row) => row.id === accountId);
        return account ? account.name : accountId;
      },
      sortValue(user, key) {
        if (key === 'account') {
          return String(this.accountName(user.account) || '').toLowerCase();
        }
        if (key === 'accessLvl') {
          return user.accessLvl === 1 ? 1 : 0;
        }
        if (key === 'hasClerk') {
          return user.hasClerk ? 1 : 0;
        }
        if (key === 'updated') {
          return String(user.updated || '');
        }
        return String(user[key] || '').toLowerCase();
      },
      sortBy(key) {
        if (this.sortKey === key) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
          return;
        }
        this.sortKey = key;
        this.sortDir = key === 'updated' ? 'desc' : 'asc';
      },
      sortMarker(key) {
        if (this.sortKey !== key) return '';
        return this.sortDir === 'asc' ? ' ↑' : ' ↓';
      },
      sortAria(key) {
        if (this.sortKey !== key) return 'none';
        return this.sortDir === 'asc' ? 'ascending' : 'descending';
      },
      savePhone(user, event) {
        const phone = normalizePhone(event.target.value);
        const current = normalizePhone(user.phone);
        if (phone === current) {
          event.target.value = current;
          return;
        }
        this.tableError = '';
        this.savingPhoneId = user.id;
        axios.post(process.env.VUE_APP_API_ROOT + '/users-admin.php', {
          action: 'updatePhone',
          id: user.id,
          phone
        })
        .then((response) => {
          if (response.data && response.data.success) {
            user.phone = response.data.user && response.data.user.phone
              ? response.data.user.phone
              : phone;
            event.target.value = user.phone;
          } else {
            event.target.value = current;
            this.tableError = (response.data && response.data.friendly && response.data.friendly.da)
              || 'Telefonnummeret kunne ikke gemmes.';
          }
        })
        .catch((error) => {
          event.target.value = current;
          const data = error.response && error.response.data;
          this.tableError = (data && data.friendly && data.friendly.da)
            || 'Telefonnummeret kunne ikke gemmes.';
        })
        .finally(() => {
          this.savingPhoneId = 0;
        });
      },
      loadAdminData() {
        axios.get(process.env.VUE_APP_API_ROOT + '/users-admin.php')
        .then(response => {
            this.accounts = response.data.accounts || [];
            this.users = response.data.users || [];
        })
        .catch((error) => {
          console.error('Kunne ikke hente brugere:', error);
        });
      },
      createUser() {
        this.formError = '';
        this.formSuccess = '';
        if (this.form.allowPasswordLogin && String(this.form.password).length < 4) {
          this.formError = 'Angiv en kode på mindst 4 tegn.';
          return;
        }
        this.saving = true;
        axios.post(process.env.VUE_APP_API_ROOT + '/users-admin.php', {
          email: this.form.email,
          account: this.form.account,
          displayName: this.form.displayName,
          phone: normalizePhone(this.form.phone),
          accessLvl: this.form.isAdmin ? 1 : 0,
          loginType: this.form.allowPasswordLogin ? 'password' : 'google',
          password: this.form.allowPasswordLogin ? this.form.password : ''
        })
        .then((response) => {
          if (response.data && response.data.success) {
            const loginHint = response.data.user.hasPassword
              ? ' (Google + mail/kode)'
              : ' (Google)';
            this.formSuccess = 'Bruger oprettet: ' + response.data.user.email + loginHint;
            this.form.email = '';
            this.form.displayName = '';
            this.form.phone = '';
            this.form.password = '';
            this.form.allowPasswordLogin = false;
            this.form.isAdmin = false;
            this.loadAdminData();
          } else {
            this.formError = (response.data && response.data.friendly && response.data.friendly.da)
              || 'Kunne ikke oprette bruger.';
          }
        })
        .catch((error) => {
          const data = error.response && error.response.data;
          this.formError = (data && data.friendly && data.friendly.da)
            || 'Kunne ikke oprette bruger.';
        })
        .finally(() => {
          this.saving = false;
        });
      }
  }
})
</script>

<style scoped>
.table-container :deep(.table) {
  font-size: 0.85rem;
}
.phone-cell {
  min-width: 8.5rem;
}
.phone-cell .input {
  height: 1.75em;
  min-height: 1.75em;
  padding-top: 0;
  padding-bottom: 0;
  font-size: inherit;
}
th.is-clickable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
th.is-clickable:hover {
  background-color: #f5f5f5;
}
</style>
